"use client";

import { useState, type FormEvent } from "react";
import { FormConsentNote } from "@/components/legal/FormConsentNote";

/* Compact newsletter signup for the footer. POSTs to the apex-flow-labs
 * lead-magnet endpoint with `source: 'footer-newsletter'` so backend's
 * Klaviyo flow can route it to the right list (general updates, not a
 * funnel-specific list). */

const BACKEND_BASE = (
  process.env.NEXT_PUBLIC_BACKEND_API_BASE || "https://www.apexflowlabs.com"
).replace(/\/$/, "");

export function FooterNewsletter() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    const trimmed = email.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setError("That doesn't look like an email.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const backendRes = await fetch(
        `${BACKEND_BASE}/api/v1/lead-magnets/subscribe`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            email: trimmed,
            lead_magnet_slug: "footer-newsletter",
            source: "footer-newsletter",
          }),
        },
      ).catch(() => null);

      if (!backendRes || !backendRes.ok) {
        /* Local Resend fallback — keeps signup working until backend
         * deploys its lead-magnet endpoint. */
        await fetch(`/api/free-access`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            email: trimmed,
            productTitle: "Field Notes — Welcome",
            accessUrl: "https://digital.apexflowlabs.com/blog",
            source: "footer-newsletter",
          }),
        }).catch(() => {});
      }
      setDone(true);
    } catch {
      /* Non-blocking — buyer doesn't care about transient errors. */
      setDone(true);
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div
        className="font-mono text-[11px] uppercase text-white"
        style={{ letterSpacing: "0.12em" }}
      >
        ✓ You're in. First field note hits Saturday.
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-2">
      <div
        className="font-mono text-[11px] uppercase text-white mb-2"
        style={{ letterSpacing: "0.14em" }}
      >
        ↳ Field notes · Saturdays only
      </div>
      <div className="flex gap-2">
        <label htmlFor="footer-newsletter-email" className="sr-only">
          Email address for the newsletter
        </label>
        <input
          id="footer-newsletter-email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@domain.com"
          autoComplete="email"
          required
          className="flex-1 rounded-lg border bg-black/40 px-3 py-2 text-[13px] outline-none focus:border-[#d9a35a]/60"
          style={{
            borderColor: "rgba(255,255,255,0.1)",
            color: "var(--color-fg)",
          }}
        />
        <button
          type="submit"
          disabled={busy}
          className="rounded-lg px-4 py-2 text-[13px] font-medium disabled:opacity-50"
          style={{
            background: "var(--color-accent)",
            color: "#000",
          }}
        >
          {busy ? "Sending…" : "Subscribe"}
        </button>
      </div>
      {error && (
        <p role="alert" className="text-[12px]" style={{ color: "#fca5a5" }}>
          {error}
        </p>
      )}
      <FormConsentNote kind="newsletter" />
    </form>
  );
}
